<script setup lang="ts">
import { computed, defineProps } from 'vue'
import BaseInputWrapper from './BaseInputWrapper.vue'
import { twMerge } from 'tailwind-merge'
import { InputSize } from '../../typings'
import { useStockInvestmentStore } from '../../utilities/investment-process/useStockInvestment'
import { storeToRefs } from 'pinia'
import { getCampaignData } from '../../utilities/investment-process/useCampaign'

const props = withDefaults(
    defineProps<{
        modelValue: number
        label?: string
        name: string
        rules?: any
        disabled: boolean
        size: InputSize
        max: number
        placeholder?: string
        classes?: string
        type: 'shares' | 'amount'
    }>(),
    { size: 'md', disabled: false, placeholder: '0', type: 'shares' }
)

const emit = defineEmits(['update:modelValue'])
const useInvestmentStore = useStockInvestmentStore()
const { minimumInvestmentType } = storeToRefs(useInvestmentStore)
const campaignData = getCampaignData()

const inputSizeClasses = computed(() => {
    const config: { [key: InputSize]: string } = {
        xs: 'pr-6 text-xs py-1',
        sm: 'pr-8 text-sm py-1',
        md: 'pr-10 text-md py-2',
        lg: 'text-lg pr-12 text-lg py-2',
        xl: 'text-xl sm:text-3xl py-4',
    }
    return config[props.size]
})

const placeholderSizeClasses = computed(() => {
    const config: { [key: InputSize]: string } = {
        xs: 'sm:text-xs',
        sm: 'sm:text-sm',
        md: 'sm:text-md',
        lg: 'sm:text-lg',
        xl: 'sm:text-xl',
    }
    return config[props.size]
})

const disabledClasses = computed(() => {
    return props.disabled ? 'bg-gray-50 border-gray-200' : ''
})

const internalValue = computed({
    get() {
        return props.modelValue
    },
    set(value) {
        emit('update:modelValue', value)
    },
})
</script>

<template>
    <BaseInputWrapper :name="props.name" :label="props.label">
        <div class="relative flex items-center rounded-md shadow-sm">
            <input
                :id="props.name"
                ref="inputRef"
                v-model="internalValue"
                v-bind="$attrs"
                type="number"
                :disabled="disabled"
                :name="props.name"
                min="0"
                :class="
                    twMerge(
                        'block w-full rounded-md border-gray-300 pl-6 text-left text-gray-600 ring-transparent focus:border focus:border-primary focus:ring-primary',
                        inputSizeClasses,
                        disabledClasses,
                        classes
                    )
                "
                :placeholder="placeholder"
                autocomplete="off"
                @click="$event.target.select()" />
            <div class="pointer-events-none absolute right-2 flex h-2/3 items-center bg-white">
                <span class="text-sm text-gray-500">{{
                    type === 'shares' ? $t('investment_process.stock') : campaignData.asset_data.currency_string
                }}</span>
            </div>
        </div>
    </BaseInputWrapper>
</template>

<style scoped>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

/* For Firefox */
input[type='number'] {
    -moz-appearance: textfield;
}
</style>
